import React from 'react';
import { Folder, FolderPlus, Loader2, ArrowRight, Plus, CopyPlus } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { useAddPublicImageToProject } from '@/hooks/runware/useAddPublicImageToProject';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';

interface PublicImageAddToProjectButtonProps {
  itemId: string;
  forceWhiteText?: boolean;
  dropdownLabel?: string;
  variant?: 'default' | 'storage' | 'auto' | 'compact';
  
  // Theme customization for compact variant
  gradientFrom?: string;
  gradientTo?: string;
  accentColor?: string;
  iconColor?: string;
  borderColor?: string;
  hoverBorderColor?: string;
  
  // Content customization for compact variant
  addText?: string;
  manageText?: string;
  addDescription?: string;
  manageDescription?: string;
}

const PublicImageAddToProjectButton: React.FC<PublicImageAddToProjectButtonProps> = ({ 
  itemId, 
  forceWhiteText, 
  dropdownLabel = "Add to Project",
  variant = 'auto',
  
  // Theme defaults - purple gradient for projects
  gradientFrom = 'from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30',
  gradientTo = 'hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40',
  accentColor = 'from-purple-400 to-violet-500',
  iconColor = 'text-purple-600 dark:text-purple-400',
  borderColor = 'border-purple-200/60 dark:border-purple-700/40',
  hoverBorderColor = 'hover:border-purple-300/70 dark:hover:border-purple-600/50',
  
  // Content defaults
  addText = 'Add to Project',
  manageText = 'Add to Project',
  addDescription = 'Copy to a project',
  manageDescription = 'Copy to a project'
}) => {
  const location = useLocation();
  const { data: workbookData, isLoading: isLoadingProjects } = useWorkbookQuery({ 
    filterByStatus: 'all' // Get all non-archived projects
  });
  
  // Use only the public image hook - no private image hook initialization
  const {
    copyToProject,
    isPending,
    isInAnyProject
  } = useAddPublicImageToProject(itemId);
  
  const projects = workbookData?.projects || [];

  // Smart context detection for optimal styling
  const isInStorageContext = variant === 'storage' || 
    (variant === 'auto' && location.pathname.includes('/storage'));

  // Determine button styling based on context and state
  const getButtonStyling = () => {
    if (isInStorageContext) {
      // Storage context: Same glass background, but force white icons for visibility
      return {
        bgClass: "bg-white/20 dark:bg-black/20",
        borderClass: "border-white/20 dark:border-transparent",
        iconColor: "text-white",
        shadow: "shadow-lg"
      };
    } else {
      // Other contexts: Always white icons (no purple state)
      return {
        bgClass: "bg-white/20 dark:bg-black/20",
        borderClass: "border-white/20 dark:border-transparent",
        iconColor: forceWhiteText ? "text-white" : "text-gray-700 dark:text-white",
        shadow: "shadow-lg"
      };
    }
  };

  const styling = getButtonStyling();

  const handleProjectSelect = async (projectId: string) => {
    if (isPending) return;
    await copyToProject(projectId);
  };
  
  const isAnythingLoading = isLoadingProjects;

  // Render compact variant differently
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "w-full relative overflow-hidden border-2 transition-all duration-300",
              `bg-gradient-to-br ${gradientFrom}`,
              borderColor,
              gradientTo,
              hoverBorderColor,
              "shadow-lg hover:shadow-xl",
              "group active:scale-[0.98]",
              "rounded-lg"
            )}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            disabled={isAnythingLoading}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Subtle top accent gradient */}
            <div className={cn("absolute top-0 left-0 right-0 h-1 bg-gradient-to-r", accentColor)} />
            
            <div className="p-3">
              <div className="flex items-center gap-2">
                {/* Icon */}
                <div className={cn(
                  "flex-shrink-0 rounded-lg transition-colors duration-300",
                  "bg-white/60 dark:bg-white/10",
                  "group-hover:bg-white/80 dark:group-hover:bg-white/15",
                  "p-1.5"
                )}>
                  <Folder className={cn("h-4 w-4", iconColor)} />
                </div>
                
                {/* Content */}
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {addText}
                    </h5>
                    {isAnythingLoading && (
                      <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {addDescription}
                  </p>
                </div>
                
                {/* Action indicator - always shows plus for public images */}
                <div className="flex-shrink-0">
                  <Plus className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
          </motion.button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="start" className="w-64" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isAnythingLoading ? (
            <div className="flex items-center justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            // Show project list as simple links
            <>
              {projects.map((project) => {
                const isDisabled = isPending;
                
                return (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => {
                      if (isDisabled) return;
                      handleProjectSelect(project.id);
                    }}
                    disabled={isDisabled}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-950/20"
                  >
                    <div className="flex items-center justify-center w-4 h-4">
                      <CopyPlus className="h-3 w-3 text-gray-400" />
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {project.title}
                    </span>
                    <ArrowRight className="h-3 w-3 text-orange-500" />
                  </DropdownMenuItem>
                );
              })}
              
              {projects.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No projects available
                </div>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default icon variant
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "inline-flex items-center justify-center p-1 rounded-full",
              styling.bgClass,
              "border",
              styling.borderClass,
              styling.shadow,
              "backdrop-blur-lg",
              "hover:scale-105 active:scale-95 transition-transform duration-200"
            )}
            onClick={(e) => e.stopPropagation()}
            disabled={isPending}
          >
            <Folder size={20} className={styling.iconColor} />
          </motion.button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-80" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isAnythingLoading ? (
            <div className="flex items-center justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            // Show project list as simple links
            <>
              {projects.map((project) => {
                const isDisabled = isPending;
                
                return (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => {
                      if (isDisabled) return;
                      handleProjectSelect(project.id);
                    }}
                    disabled={isDisabled}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-950/20"
                  >
                    <div className="flex items-center justify-center w-4 h-4">
                      <CopyPlus className="h-3 w-3 text-gray-400" />
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {project.title}
                    </span>
                    <ArrowRight className="h-3 w-3 text-orange-500" />
                  </DropdownMenuItem>
                );
              })}
              
              {projects.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No projects available
                </div>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default PublicImageAddToProjectButton; 